
import { WordPair } from "@/types/game";
import { checkWordsMatch } from "@/utils/gameUtils";
import { MAX_ROUNDS } from "@/utils/gameConstants";
import { handleWordMatch, handleMaxRoundsReached } from "@/utils/gameOutcomeHandlers";
import { prepareNextRound } from "@/utils/roundPreparation";
import { calculateAndUpdateProximity } from "@/utils/proximityUtils";
import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel, captureStateSnapshot } from "@/utils/debugUtils";

/**
 * Processes the current round after both words are submitted
 */
export const processRoundCompletion = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string,
  aiExplanation: string | undefined,
  usedWords: Set<string>,
  setRounds: (rounds: WordPair[] | ((prev: WordPair[]) => WordPair[])) => void,
  setUsedWords: (usedWords: Set<string> | ((prev: Set<string>) => Set<string>)) => void,
  setCurrentWord: (word: string) => void,
  setIsRevealed: (isRevealed: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setGameStatus: (status: string) => void,
  setUserHasSubmitted: (hasSubmitted: boolean) => void,
  setProcessingComplete: (complete: boolean) => void,
  toast: any
) => {
  debugLog(DebugLevel.INFO, "roundProcessors", `Starting processRoundCompletion for round ${roundNumber}`, {
    userWord, 
    aiWord,
    hasExplanation: !!aiExplanation
  });
  
  // Check if the words match (ignoring case)
  const normalizedUserWord = userWord.toLowerCase();
  const wordsMatch = checkWordsMatch(normalizedUserWord, aiWord);
  debugLog(DebugLevel.INFO, "roundProcessors", `Words match check: ${wordsMatch ? "YES" : "NO"}`);
  
  // Create new round object (proximity will be updated later)
  const newRound: WordPair = {
    userWord,
    aiWord,
    aiExplanation,
    proximityScore: wordsMatch ? 100 : undefined // If words match, set proximity to 100 immediately
  };

  debugLog(DebugLevel.INFO, "roundProcessors", "Adding new round to state:", newRound);

  // Update state with the new round - make sure this only adds exactly ONE round to the array
  setRounds(prev => {
    // First check if this round already exists in the array (by index)
    const existingRoundIndex = prev.findIndex((_, idx) => idx === roundNumber - 1);
    
    if (existingRoundIndex !== -1) {
      // Update the existing round instead of adding a new one
      const updatedRounds = [...prev];
      updatedRounds[existingRoundIndex] = newRound;
      return updatedRounds;
    } else {
      // Make sure we're only adding one round
      // If rounds.length is 0, we're adding round 1 (index 0)
      // If rounds.length is 1, we're adding round 2 (index 1), etc.
      if (prev.length === roundNumber - 1) {
        return [...prev, newRound];
      } else {
        // This shouldn't happen, but just in case, return the existing rounds
        debugLog(DebugLevel.ERROR, "roundProcessors", `Unexpected round number discrepancy. Expected round ${roundNumber}, but current rounds array has ${prev.length} items.`);
        return prev;
      }
    }
  });
  
  setUsedWords((prev: Set<string>) => new Set([...prev, normalizedUserWord, aiWord.toLowerCase()]));
  setCurrentWord("");
  setIsRevealed(true);
  setBotHasChosenWord(false);
  setUserHasSubmitted(false); // Reset user submission state for next round

  debugLog(DebugLevel.INFO, "roundProcessors", "Updated basic state values for round completion");
}

/**
 * Handles round outcome after words have been processed
 */
export const handleRoundOutcome = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string,
  usedWords: Set<string>,
  wordsMatch: boolean,
  setRounds: (rounds: WordPair[] | ((prev: WordPair[]) => WordPair[])) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setGameStatus: (status: string) => void,
  setProcessingComplete: (complete: boolean) => void,
  toast: any
) => {
  // Check if game is over (words match)
  if (wordsMatch) {
    debugLog(DebugLevel.INFO, "roundProcessors", "Words match! Game is converging.");
    await handleWordMatch(
      gameId, 
      roundNumber, 
      userWord.toLowerCase(), 
      setGameOver, 
      setConvergenceRound, 
      setGameStatus
    );
    setProcessingComplete(true);
  } 
  // Check if max rounds reached
  else if (roundNumber >= MAX_ROUNDS) {
    debugLog(DebugLevel.INFO, "roundProcessors", "Max rounds reached. Game will end after this round.");
    // Even for the last round, calculate proximity before ending game
    await calculateAndUpdateProximity(
      gameId,
      roundNumber,
      userWord,
      aiWord,
      setRounds
    );
    
    await handleMaxRoundsReached(
      gameId, 
      setGameOver, 
      setGameStatus, 
      toast
    );
    setProcessingComplete(true);
  } 
  // Continue to next round
  else {
    debugLog(DebugLevel.INFO, "roundProcessors", `Game continues. Preparing for round ${roundNumber + 1}`);
    await prepareForNextRound(
      gameId,
      roundNumber,
      userWord,
      aiWord,
      Array.from(usedWords),
      setRounds,
      setBotIsThinking,
      setBotHasChosenWord,
      setShouldAutoFocus,
      setProcessingComplete,
      toast
    );
  }
}

/**
 * Prepares for the next round by creating it in the database
 */
export const prepareForNextRound = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string,
  usedWordsArray: string[],
  setRounds: (rounds: WordPair[] | ((prev: WordPair[]) => WordPair[])) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setProcessingComplete: (complete: boolean) => void,
  toast: any
) => {
  // CRITICAL FIX: Force create the next round record before we proceed
  // This ensures that the database has the new round immediately
  try {
    const { error: createRoundError } = await supabase
      .from("rounds")
      .insert({ 
        game_id: gameId, 
        round_number: roundNumber + 1
      })
      .select("id")
      .maybeSingle();
      
    if (createRoundError) {
      debugLog(DebugLevel.WARN, "roundProcessors", `Error creating round ${roundNumber + 1}, it may already exist:`, createRoundError);
      // Check if it already exists
      const { data: existingRound } = await supabase
        .from("rounds")
        .select("id")
        .eq("game_id", gameId)
        .eq("round_number", roundNumber + 1)
        .maybeSingle();
        
      if (existingRound) {
        debugLog(DebugLevel.INFO, "roundProcessors", `Round ${roundNumber + 1} already exists, continuing...`);
      } else {
        throw createRoundError; // Re-throw if it doesn't exist and we couldn't create it
      }
    } else {
      debugLog(DebugLevel.INFO, "roundProcessors", `Successfully created round ${roundNumber + 1} in database`);
    }
  } catch (createError) {
    debugLog(DebugLevel.ERROR, "roundProcessors", `Critical error creating round ${roundNumber + 1}:`, createError);
    toast({
      variant: "destructive",
      title: "Error",
      description: "There was a problem creating the next round."
    });
    setProcessingComplete(true);
    return;
  }
  
  // CRITICAL FIX: Ensure the round is properly prepared
  try {
    await prepareNextRound(
      gameId,
      roundNumber,
      userWord,
      aiWord,
      usedWordsArray,
      setRounds,
      setBotIsThinking,
      setBotHasChosenWord,
      setShouldAutoFocus,
      setProcessingComplete,
      toast
    );
    debugLog(DebugLevel.INFO, "roundProcessors", `Successfully prepared round ${roundNumber + 1}`);
  } catch (prepareError) {
    debugLog(DebugLevel.ERROR, "roundProcessors", "Error preparing next round:", prepareError);
    toast({
      variant: "destructive",
      title: "Error",
      description: "There was a problem preparing the next round."
    });
    
    // Try to recover by ensuring the next round exists anyway
    try {
      debugLog(DebugLevel.WARN, "roundProcessors", "Attempting recovery by verifying next round exists");
      // Verify next round exists in database
      const { data: nextRound } = await supabase
        .from("rounds")
        .select("id")
        .eq("game_id", gameId)
        .eq("round_number", roundNumber + 1)
        .maybeSingle();
        
      if (!nextRound) {
        debugLog(DebugLevel.WARN, "roundProcessors", "Next round does not exist, creating it");
        await supabase
          .from("rounds")
          .insert([{ 
            game_id: gameId, 
            round_number: roundNumber + 1
          }]);
      }
      
      // Reset states for next round
      setBotIsThinking(false);
      setShouldAutoFocus(true);
      debugLog(DebugLevel.INFO, "roundProcessors", "Recovery attempt completed");
    } catch (recoveryError) {
      debugLog(DebugLevel.CRITICAL, "roundProcessors", "Recovery attempt failed:", recoveryError);
    } finally {
      setProcessingComplete(true);
    }
  }
}
